import React, { Fragment, useState } from "react";
import ProductAddToCart from "../productPage/ProductAddToCart";
import { useSelector, useDispatch } from "react-redux";
import { guardCustomerAction } from "../../../utils/emailVerification";
import { resolveProductActiveStatus } from "../../../utils/productStatus";
import { canUseCustomerFeatures } from "../../../utils/userMode";
import ProductStatusBadge from "../../productStatus/ProductStatusBadge";
import Detailes, { StaffLocationChip } from "./cardDeailes";
import PromoBadge from "./PromoBadge";
import PackagePromoPopUp from "./PackagePromoPopUp";
import { findPackagePromo } from "../sales/promotionUtils";
import { Heart } from "lucide-react";

export { resolveProductActiveStatus } from "../../../utils/productStatus";

const Card = ({
  element,
  showPrice = true,
  productLocationPopUp,
  productPopUp,
  addToShopListPopUp,
  comparePricesPopUp,
  editImagesPopUp,
  editSettingsPopUp,
  onAdminStatusClick,
  statusPending = false,
  listHeartActive = false,
  alwaysShowBrand = false,
}) => {
  const WeightJump = element.weightJump > 0 ? element.weightJump : 1;
  const dispatch = useDispatch();
  const [packagePopUp, setPackagePopUp] = useState(false);
  // The package promotion is not necessarily promotions[0].
  const packagePromo = findPackagePromo(element);
  const badgePromo = packagePromo ?? element.pricing?.promotions?.[0];

  const userState = useSelector((state) => state.user);
  const role = userState.role;
  const isClientView = useSelector((state) => state.category.isClientView);
  const customerFeatures = canUseCustomerFeatures(
    role,
    isClientView,
    userState.user,
  );
  const badgeStatus = resolveProductActiveStatus(element);
  // Admin always — keep status / edit overlays in customer view too.
  const showStatusBadge = !!badgeStatus && !!role;

  const inCart = useSelector((state) =>
    state.productsInCart.productsInCart.find(
      (item) => item.productId === element.productId,
    ),
  );

  const AddToShopListIfUser = () => {
    if (guardCustomerAction(dispatch, { user: userState })) {
      return;
    }
    addToShopListPopUp();
  };

  // A cart line stuck at 0 means the server rejected it as unavailable — treat as out of stock.
  // allowOrderWhenOos products stay orderable and must not show OOS to the customer.
  const allowOrderWhenOos = !!element?.allowOrderWhenOos;
  const cartOutOfStock =
    !!inCart && inCart.quantity === 0 && !allowOrderWhenOos;

  const showCustomerCartRow =
    customerFeatures &&
    !cartOutOfStock &&
    (allowOrderWhenOos ||
      (element?.availability?.availableQuantity ?? 0) >= WeightJump ||
      (inCart && inCart.quantity > 0));

  const isOutOfStock = customerFeatures && !showCustomerCartRow;

  // Package buy button ("לרכישה במארז") follows retail cart availability.
  const showPackageAction = !!packagePromo && showCustomerCartRow;

  const listCornerButton = showCustomerCartRow ? (
    <button
      type="button"
      className={`item-card-list-btn${listHeartActive ? " item-card-list-btn--active" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        AddToShopListIfUser();
      }}
      data-tip
      data-for="registerTipAddToList"
      aria-label="הוספה לרשימה"
    >
      <Heart
        size={17}
        strokeWidth={1.75}
        aria-hidden
        fill={listHeartActive ? "currentColor" : "none"}
      />
    </button>
  ) : null;

  return (
    <Fragment>
      <div className={role ? "item-card role" : "item-card"}>
        {role && isClientView ? (
          <div className="item-card__admin-top">
            {showStatusBadge ? (
              <ProductStatusBadge
                status={badgeStatus}
                pending={statusPending}
                onStatusChange={
                  onAdminStatusClick
                    ? (nextStatus) => onAdminStatusClick(element, nextStatus)
                    : undefined
                }
              />
            ) : null}
            <StaffLocationChip
              element={element}
              weightJump={WeightJump}
              productLocationPopUp={productLocationPopUp}
            />
          </div>
        ) : showStatusBadge ? (
          <ProductStatusBadge
            status={badgeStatus}
            pending={statusPending}
            onStatusChange={
              onAdminStatusClick
                ? (nextStatus) => onAdminStatusClick(element, nextStatus)
                : undefined
            }
          />
        ) : null}
        {element.weightJump > 1 && (
          <span className="sales-jump tag tag--meta">
            נמכר ב-{element.weightJump} יחידות
          </span>
        )}

        {customerFeatures &&
        !allowOrderWhenOos &&
        (element?.availability?.availableQuantity ?? 0) === 0 ? (
          <div className="top-card-section">
            <p className="inventoryEnd">חסר</p>
          </div>
        ) : null}

        <Detailes
          element={element}
          showPrice={showPrice}
          inCart={inCart}
          productPopUp={productPopUp}
          comparePricesPopUp={comparePricesPopUp}
          editImagesPopUp={editImagesPopUp}
          editSettingsPopUp={editSettingsPopUp}
          showProductEdit={!!role}
          isStaff={!!role}
          weightJump={WeightJump}
          productLocationPopUp={productLocationPopUp}
          showStaffLocationOnImage={!!role && !isClientView}
          imageCornerActions={listCornerButton}
          promoBadge={
            <PromoBadge
              promotion={badgePromo}
              onPackageBuy={
                showPackageAction ? () => setPackagePopUp(true) : undefined
              }
            />
          }
          footerSlot={
            isOutOfStock ? (
              <div className="oos-label">חסר במלאי</div>
            ) : showCustomerCartRow ? (
              <ProductAddToCart element={element} haveInfo={false} retailCard />
            ) : null
          }
          alwaysShowBrand={alwaysShowBrand}
        />

        <div className="details">
          {element.DisableProductId > 1 && element.DisableProductId !== 6 && (
            <p className="inventoryEnd disable-product">
              {element.DisableProduct}
            </p>
          )}

          {element.DisableProductId === 6 && role && (
            <p className="inventoryEnd disable-product">
              {element.DisableProduct}
            </p>
          )}
        </div>
      </div>
      {packagePopUp && packagePromo ? (
        <PackagePromoPopUp
          product={element}
          promo={packagePromo}
          close={() => setPackagePopUp(false)}
        />
      ) : null}
    </Fragment>
  );
};

export default React.memo(Card);
